import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Check,
  Copy,
  Code,
  Link as LinkIcon,
  Twitter,
  Linkedin,
} from 'lucide-react';
import { m, AnimatePresence } from 'framer-motion';

interface ShareDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  modelUrl: string;
  modelTitle: string;
  authorUsername: string;
  embedWidth?: number;
  embedHeight?: number;
}

export function ShareDialog({
  open,
  onOpenChange,
  modelUrl,
  modelTitle,
  authorUsername,
  embedWidth = 800,
  embedHeight = 600,
}: ShareDialogProps) {
  const [copiedLink, setCopiedLink] = useState(false);
  const [copiedEmbed, setCopiedEmbed] = useState(false);

  const fullUrl = `${window.location.origin}${modelUrl}`;
  const embedCode = `<iframe src="${fullUrl}?embed=true" width="${embedWidth}" height="${embedHeight}" frameborder="0" allowfullscreen></iframe>`;

  const handleCopyLink = async () => {
    await navigator.clipboard.writeText(fullUrl);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  const handleCopyEmbed = async () => {
    await navigator.clipboard.writeText(embedCode);
    setCopiedEmbed(true);
    setTimeout(() => setCopiedEmbed(false), 2000);
  };

  const shareText = `Check out "${modelTitle}" by @${authorUsername}`;
  const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(fullUrl)}`;
  const linkedinUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(fullUrl)}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-border bg-background sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="text-foreground">Share Model</DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Share "{modelTitle}" with others or embed it on your website.
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="link" className="w-full">
          <TabsList className="grid w-full grid-cols-2 bg-muted">
            <TabsTrigger
              value="link"
              className="text-muted-foreground data-[state=active]:bg-background data-[state=active]:text-foreground"
            >
              <LinkIcon className="mr-2 h-4 w-4" />
              Link
            </TabsTrigger>
            <TabsTrigger
              value="embed"
              className="text-muted-foreground data-[state=active]:bg-background data-[state=active]:text-foreground"
            >
              <Code className="mr-2 h-4 w-4" />
              Embed
            </TabsTrigger>
          </TabsList>

          <TabsContent value="link" className="space-y-4 pt-4">
            {/* Direct Link */}
            <div className="grid gap-2">
              <Label className="text-muted-foreground">Direct Link</Label>
              <div className="flex gap-2">
                <Input
                  value={fullUrl}
                  readOnly
                  className="border-border bg-muted font-mono text-sm text-foreground"
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handleCopyLink}
                  className="shrink-0 border-border bg-transparent text-muted-foreground hover:bg-muted hover:text-foreground"
                >
                  <AnimatePresence mode="wait" initial={false}>
                    {copiedLink ? (
                      <m.div
                        key="check"
                        initial={{ scale: 0.95, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.95, opacity: 0 }}
                        transition={{ duration: 0.15 }}
                      >
                        <Check className="h-4 w-4 text-green-500" />
                      </m.div>
                    ) : (
                      <m.div
                        key="copy"
                        initial={{ scale: 0.95, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.95, opacity: 0 }}
                        transition={{ duration: 0.15 }}
                      >
                        <Copy className="h-4 w-4" />
                      </m.div>
                    )}
                  </AnimatePresence>
                </Button>
              </div>
            </div>

            {/* Social Share */}
            <div className="grid gap-2">
              <Label className="text-muted-foreground">Share on Social</Label>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.open(twitterUrl, '_blank')}
                  className="flex-1 border-border bg-transparent text-muted-foreground hover:bg-muted hover:text-foreground"
                >
                  <Twitter className="mr-2 h-4 w-4" />
                  Twitter
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.open(linkedinUrl, '_blank')}
                  className="flex-1 border-border bg-transparent text-muted-foreground hover:bg-muted hover:text-foreground"
                >
                  <Linkedin className="mr-2 h-4 w-4" />
                  LinkedIn
                </Button>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="embed" className="space-y-4 pt-4">
            {/* Embed Code */}
            <div className="grid gap-2">
              <Label className="text-muted-foreground">Embed Code</Label>
              <div className="relative">
                <textarea
                  value={embedCode}
                  readOnly
                  rows={3}
                  className="w-full resize-none rounded-md border border-border bg-muted p-3 font-mono text-sm text-foreground"
                />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleCopyEmbed}
                  className="absolute right-2 top-2 h-7 w-20 text-muted-foreground hover:bg-background hover:text-foreground"
                >
                  <AnimatePresence mode="wait" initial={false}>
                    {copiedEmbed ? (
                      <m.div
                        key="check"
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.8, opacity: 0 }}
                        transition={{ duration: 0.15 }}
                        className="flex items-center"
                      >
                        <Check className="mr-1 h-3 w-3 text-green-500" />
                        Copied
                      </m.div>
                    ) : (
                      <m.div
                        key="copy"
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.8, opacity: 0 }}
                        transition={{ duration: 0.15 }}
                        className="flex items-center"
                      >
                        <Copy className="mr-1 h-3 w-3" />
                        Copy
                      </m.div>
                    )}
                  </AnimatePresence>
                </Button>
              </div>
            </div>

            {/* Preview */}
            <div className="grid gap-2">
              <Label className="text-muted-foreground">Preview</Label>
              <div className="rounded-lg border border-border bg-muted p-4">
                <div className="flex aspect-video items-center justify-center rounded border border-border bg-background text-muted-foreground">
                  <span className="text-sm">
                    Embedded viewer preview ({embedWidth}x{embedHeight})
                  </span>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
